import React from 'react';
import { X, FileText, Info } from 'lucide-react';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (startingNumber: number) => void;
}

interface State {
    startingNumber: number;
}

class StartingRONumberModal extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            startingNumber: 1000,
        };
    }

    handleConfirm = () => {
        this.props.onConfirm(this.state.startingNumber);
    };

    handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(e.target.value, 10);
        if (!isNaN(value) && value >= 1) {
            this.setState({ startingNumber: value });
        }
    };

    render() {
        const { isOpen, onClose } = this.props;
        const { startingNumber } = this.state;

        if (!isOpen) return null;

        return (
            <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
                        onClick={onClose}
                    ></div>

                    {/* Modal */}
                    <div className="relative inline-block overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:w-full sm:max-w-lg">
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                                        <FileText className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900">
                                        Set Starting Repair Order Number
                                    </h3>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="px-6 py-5">
                            <div className="mb-6">
                                <div className="flex items-start p-4 mb-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <Info className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                                    <div className="text-sm text-blue-800">
                                        <p className="font-medium mb-1">This is a one-time setup</p>
                                        <p>
                                            Choose the starting number for your repair orders. After this, each new
                                            repair order will automatically increment by 1.
                                        </p>
                                    </div>
                                </div>

                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Starting Number
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    value={startingNumber}
                                    onChange={this.handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-semibold"
                                    placeholder="1000"
                                    autoFocus
                                />
                                <p className="mt-2 text-sm text-gray-500">
                                    Your first repair order will be numbered: <span className="font-semibold">#{startingNumber}</span>
                                </p>
                            </div>

                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">Example Numbering:</h4>
                                <div className="space-y-1 text-sm text-gray-600">
                                    <p>• 1st Repair Order: #{startingNumber}</p>
                                    <p>• 2nd Repair Order: #{startingNumber + 1}</p>
                                    <p>• 3rd Repair Order: #{startingNumber + 2}</p>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={this.handleConfirm}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default StartingRONumberModal;
